import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
} from "@/components/ui/breadcrumb";
import { useTheme } from "@/layout/ThemeProvider";
import { useLogout, usePermissions } from "react-admin";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import axiosInstance from "@/utils/axiosInstance";
import { Settings } from "@/interface/entityInterface";
import { useUserContext } from "@/context/UserContext";
import { useSipContext } from "@/context/SipContext";
import { disconnectTaxiSocket } from "@/hooks/useTaxiSocket";
import NotificationsPopover from "./NotificationPopover";

const AppBar = () => {
  const sipContext = useSipContext();
  const { setTheme } = useTheme();
  const logout = useLogout();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { decodedToken } = useUserContext();
  const [profile, setProfile] = useState<Settings | null>(null);
  
  useEffect(() => {
    if (!decodedToken?.settings.id) return;
    const unsub = async () => {
      const { data } = await axiosInstance.get(
        `/settings/${decodedToken.settings.id}`,
      );
      const { id, company_logo, name, settings_type, api_key, currency } = data;
      setProfile({ id, company_logo, name, settings_type, api_key, currency });
    };

    unsub();
  }, [decodedToken?.settings.id]);

  const [userName, setUserName] = useState("Loading...");

  const settings = () => {
    navigate("/settings");
  };

  const support = () => {
    navigate("/support");
  };

  useEffect(() => {
    if (!decodedToken?.user.id) return;
    try {
      if (decodedToken) {
        setUserName(
          decodedToken.user.firstname + " " + decodedToken.user.lastname ||
            decodedToken.user.email ||
            "User",
        );
      } else {
        setUserName("No user");
      }
    } catch {
      // setUserName("Error");
    }
  }, [decodedToken]);

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b px-4">
      <div className="flex items-center gap-2">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink href="/">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="flex items-center gap-2">
        <NotificationsPopover />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <span className="sr-only">Toggle theme</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme("light")}>
              Light
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")}>
              Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")}>
              System
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Avatar className="w-5 h-5">
                {profile && profile.company_logo && (
                  <AvatarImage src={profile.company_logo} />
                )}
                <AvatarFallback>
                  {profile?.name
                    ? profile?.name.split(" ")[0][0] +
                      profile?.name.split(" ")[1][0]
                    : ""}
                </AvatarFallback>
              </Avatar>
              {userName}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <p className="text-sm text-gray-500 ms-2">{decodedToken?.user.email}</p>
            {permissions && permissions.user_role == "owner" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={settings}>
                    Settings
                    <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={support}>Support</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/users")}>
              Users
            </DropdownMenuItem>
            <DropdownMenuItem disabled>API</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                // Disconnect SIP before logout
                console.log("Disconnecting SIP before logout");
                sipContext.disconnect();

                // Disconnect websocket before logout
                console.log("Disconnecting taxi websocket before logout");
                disconnectTaxiSocket();

                // Small delay to ensure disconnections complete
                setTimeout(() => {
                  logout();
                }, 200);
              }}
            >
              Log out
              <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default AppBar;
